import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ChevronRight, Check, X, Plus, Minus } from "lucide-react";
import { api, fmt } from "@/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";

const PUJAS = ["Ganesh Puja","Lakshmi Puja","Shiv Puja","Durga Puja","Satyanarayan Puja","Hanuman Puja","Griha Pravesh","Vastu Puja","Havan","Navratri Puja","Diwali Puja","Janmashtami Puja","Other Puja"];
const OCCASIONS = ["Daily Puja","Festival","Special Puja","Griha Pravesh","Wedding","Birthday","Business / Shop Opening","Other"];
const FAMILY = ["1-2","3-5","6-10","10+"];
const KTYPE = ["Simple","Complete","Premium"];

export default function KitFinderModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [ans, setAns] = useState({ puja:"", occasion:"", family_size:"", kit_type:"" });
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToCart } = useApp();
  const nav = useNavigate();

  const reset = () => { setStep(1); setAns({puja:"",occasion:"",family_size:"",kit_type:""}); setKit(null); };
  const close = () => { onClose(); setTimeout(reset, 250); };

  const submit = async (finalAns) => {
    setLoading(true);
    try {
      const { data } = await api.post("/kits/recommend", finalAns);
      setKit(data); setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No matching kit found. Showing a Ganesh Kit instead.");
      setStep(5);
    } finally { setLoading(false); }
  };

  const pick = (key, val) => {
    const next = { ...ans, [key]: val };
    setAns(next);
    if (key === "kit_type") submit(next);
    else setStep(step + 1);
  };

  const updateItemQty = (idx, delta) => {
    setKit(k => {
      const items = [...k.items];
      items[idx] = { ...items[idx], qty: Math.max(1, items[idx].qty + delta) };
      return { ...k, items };
    });
  };
  const removeItem = (idx) => setKit(k => ({ ...k, items: k.items.filter((_,i)=>i!==idx) }));

  const kitTotal = kit ? kit.items.reduce((s,i)=>s + (i.price * i.qty), 0) : 0;
  const savings = kit ? Math.max(0, kitTotal - (kit.price || kitTotal)) : 0;

  const addKitToCart = () => {
    if (!kit) return;
    kit.items.forEach(i => {
      addToCart({ product_id: `kit-${kit.id}-${i.name}`, name: i.name, price: i.price, image: i.image, kit: true }, i.qty);
    });
    toast.success("Kit added to cart!"); close(); nav("/cart");
  };

  const Options = ({ items, k }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map(v => (
        <button key={v} data-testid={`finder-${k}-${v.replace(/\s+/g,"-")}`}
          onClick={()=>pick(k,v)}
          className="text-left px-5 py-4 rounded-xl border border-border bg-white hover:border-primary hover:bg-muted transition-colors flex items-center justify-between group">
          <span className="font-medium">{v}</span>
          <ChevronRight className="w-4 h-4 opacity-40 group-hover:translate-x-1 group-hover:opacity-100 transition-transform"/>
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl bg-background max-h-[90vh] overflow-y-auto" data-testid="kit-finder-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary"><Sparkles className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.3em] font-semibold">Smart Puja Kit Finder</span></div>
          <DialogTitle className="font-serif text-3xl leading-tight">
            <span className="devanagari block">पूजा करनी है? पूरी सामग्री हम चुनेंगे।</span>
            <span className="block text-xl font-normal text-muted-foreground mt-1">Answer 4 quick questions to get your perfect kit</span>
          </DialogTitle>
        </DialogHeader>

        {step <= 4 && (
          <div className="mt-4">
            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              {[1,2,3,4].map(n => (
                <div key={n} className={`h-1 flex-1 rounded ${n<=step?"bg-primary":"bg-muted"}`} />
              ))}
              <span className="text-xs text-muted-foreground ml-2">Step {step}/4</span>
            </div>
            {step===1 && (<><h3 className="font-serif text-2xl mb-4">What Puja are you planning?</h3><Options items={PUJAS} k="puja"/></>)}
            {step===2 && (<><h3 className="font-serif text-2xl mb-4">Select the Occasion</h3><Options items={OCCASIONS} k="occasion"/></>)}
            {step===3 && (<><h3 className="font-serif text-2xl mb-4">Family / Group Size</h3><Options items={FAMILY} k="family_size"/></>)}
            {step===4 && (<><h3 className="font-serif text-2xl mb-4">Choose your Puja style</h3><Options items={KTYPE} k="kit_type"/></>)}
          </div>
        )}

        {loading && <div className="py-16 text-center text-muted-foreground">Curating your kit...</div>}

        {step===5 && kit && (
          <div className="mt-2" data-testid="kit-finder-result">
            <div className="rounded-2xl bg-gradient-to-br from-[#FFF6E5] to-[#FDECD0] p-5 border border-secondary/40">
              <div className="flex items-start gap-4">
                <img src={kit.image} alt="" className="w-24 h-24 rounded-xl object-cover"/>
                <div className="flex-1">
                  <div className="section-label">Recommended Kit</div>
                  <h3 className="font-serif text-2xl mt-1">{kit.name}</h3>
                  <div className="text-sm text-muted-foreground mt-1">Serves {kit.serves || ans.family_size} · {kit.items.length} items included</div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className="text-2xl font-semibold text-primary">{fmt(kit.price)}</span>
                    {kit.mrp > kit.price && <span className="line-through text-sm text-muted-foreground">{fmt(kit.mrp)}</span>}
                    {savings>0 && <span className="text-xs bg-primary text-white px-2 py-1 rounded-full">Save {fmt(savings)}</span>}
                  </div>
                </div>
              </div>
            </div>

            <h4 className="mt-6 mb-3 section-label">What's included · Customize</h4>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {kit.items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-white">
                  <img src={it.image} alt="" className="w-12 h-12 rounded object-cover"/>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{it.name}</div>
                    <div className="text-xs text-muted-foreground">{fmt(it.price)} each</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`kit-item-dec-${idx}`} onClick={()=>updateItemQty(idx,-1)} className="w-7 h-7 rounded-full border border-border grid place-items-center"><Minus className="w-3 h-3"/></button>
                    <span className="w-6 text-center font-medium">{it.qty}</span>
                    <button data-testid={`kit-item-inc-${idx}`} onClick={()=>updateItemQty(idx,1)} className="w-7 h-7 rounded-full border border-border grid place-items-center"><Plus className="w-3 h-3"/></button>
                  </div>
                  <button onClick={()=>removeItem(idx)} className="p-2 hover:text-primary"><X className="w-4 h-4"/></button>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button data-testid="add-kit-to-cart-btn" onClick={addKitToCart} className="pk-btn-primary">
                <Check className="w-4 h-4"/> Add Complete Kit to Cart
              </button>
              <button onClick={reset} className="pk-btn-secondary">Start Over</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
